import React, { useMemo } from 'react';
import { Field, FieldArray, Form, Formik } from 'formik';
import { IconButton, MessageBar, MessageBarType, SelectionMode } from 'office-ui-fabric-react';
import { DetailsRow } from 'office-ui-fabric-react/lib/DetailsList';
import { keyBy, mapValues, omit, uniq } from 'lodash';
import { Label } from 'office-ui-fabric-react/lib/Label';
import PropTypes from 'prop-types';
import { useStore } from 'react-redux';
import moment from 'moment';
import DynamicDetailsList from '../DynamicDetailsList/DynamicDetailsList';
import styles from './ContractFilterForm.styles';
import TextButton from '../TextButton';
import { pxToRem } from '../../utils';
import { pivotWider, rowTransformer } from '../DynamicDetailsList/RowTransformation';
import {
  defaultPivotWiderTransformer,
  defaultTransformer,
  getAllColumnsWithOperators,
} from '../DynamicDetailsList/ContractTransactionColumns';
import MultiPickerFormik from '../MultiPicker/MultiPickerFormik';
import theme from '../../theme.style';
import { effectiveDateColumn, expirationDateColumn } from '../DynamicDetailsList/DateColumns';

const topLevelData = ['_id', 'type', 'effectiveDate', 'terminationDate'];

const ContractFilterForm = React.forwardRef(
  ({ scopeFilter, effectiveDate, terminationDate, onSubmit }, ref) => {
    const store = useStore();
    const allColumns = useMemo(() => getAllColumnsWithOperators(true), []);
    const pivotFiltersWider = pivotWider(
      mapValues(keyBy(allColumns, 'key'), 'pivotWiderTransformer'),
      defaultPivotWiderTransformer,
      store,
      ...topLevelData,
    );

    const minDate = moment.utc(effectiveDate).startOf('day').toDate();
    const maxDate = moment.utc(terminationDate).startOf('day').toDate();

    const initialValues = {
      dimensions: allColumns.filter(c => scopeFilter?.dimensions?.includes(c.key)),
      filters:
        scopeFilter?.filters
          ?.map(pivotFiltersWider)
          .map(filter => ({ ...filter, exclude: filter.type === 'exclude' })) || [],
    };

    const createNewFilter = () => ({
      exclude: false,
      effectiveDate,
      terminationDate,
    });

    const onSubmitForm = values => {
      const dimensions = values.dimensions.map(d => d.key);
      const filters = values.filters
        .map(filter => ({
          ...filter,
          type: filter.exclude ? 'exclude' : 'include',
          effectiveDate: moment.utc(filter.effectiveDate).format('YYYY-MM-DD'),
          terminationDate: moment.utc(filter.terminationDate).format('YYYY-MM-DD'),
        }))
        .map(rowTransformer(values.dimensions, defaultTransformer, ...topLevelData));
      onSubmit({ dimensions, filters });
    };

    const rowValidator = row => {
      const errors = { rowErrors: [] };
      if (!row.effectiveDate) {
        errors.effectiveDate = 'Required';
        errors.rowErrors.push('Missing Required Fields.');
      }
      if (!row.terminationDate) {
        errors.terminationDate = 'Required';
        errors.rowErrors.push('Missing Required Fields.');
      }

      if (
        row.effectiveDate &&
        row.terminationDate &&
        moment(row.effectiveDate).isAfter(row.terminationDate)
      ) {
        errors.effectiveDate = 'Invalid Range';
        errors.terminationDate = 'Invalid Range';
        errors.rowErrors.push('Expiration Date must be after effective date.');
      }

      if (!row.exclude && !row.product?.id) {
        errors.product = 'Required';
        errors.rowErrors.push(
          'Inclusion filters must have at least one product item or list specified. Please edit the highlighted rows.',
        );
      } else if (row.exclude && row.dimensions.length === 0) {
        errors.rowErrors.push(
          'Exclusion filters must have at least one item or list specified. Please edit the highlighted rows.',
        );
      }
      if (errors.rowErrors.length === 0) return undefined;
      return errors;
    };

    const formValidator = values => {
      const filterTransformer = rowTransformer(
        values.dimensions,
        defaultTransformer,
        'exclude',
        'effectiveDate',
        'terminationDate',
      );
      const filterErrors = values.filters
        .map(f => ({ ...f, dimensions: filterTransformer(f).dimensions }))
        .map(rowValidator);
      if (filterErrors.every(e => !e)) return {};
      return { filters: filterErrors };
    };

    return (
      <Formik
        innerRef={ref}
        initialValues={initialValues}
        onSubmit={onSubmitForm}
        validate={formValidator}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, setFieldValue, errors }) => {
          // here we need to update the dimensions, but we are getting ids from the DetailsList,
          // which also includes the frozen columns in its indexing.
          const fCol = 1; // frozen column index
          const handleColumnReorder = (draggedIndex, targetIndex) => {
            const newDimensions = [...values.dimensions];
            const draggedDimension = values.dimensions[draggedIndex - fCol];

            // insert before the dropped item
            newDimensions.splice(draggedIndex - fCol, 1);
            newDimensions.splice(targetIndex - fCol, 0, draggedDimension);
            setFieldValue('dimensions', newDimensions);
          };

          const columnReorderOptions = {
            frozenColumnCountFromStart: fCol,
            frozenColumnCountFromEnd: 3,
            handleColumnReorder,
          };

          const onRenderRow = props => {
            const customStyle = {
              root: {
                backgroundColor: theme.palette.neutralWhite,
                selectors: {
                  '& .ms-DetailsRow-cell': {
                    padding: `${pxToRem(8)} ${pxToRem(4)}`,
                  },
                  '& .ms-Dropdown-container > .ms-Stack': {
                    marginBottom: `${pxToRem(0)} !important`,
                  },
                },
              },
            };
            // eslint-disable-next-line react/prop-types
            if (errors.filters && errors.filters[props.itemIndex])
              customStyle.root = {
                backgroundColor: theme.palette.accentErrorOpaque,
                ':active': { backgroundColor: theme.palette.accentErrorOpaque },
                ':hover': { backgroundColor: theme.palette.accentErrorOpaque },
              };
            return (
              <DetailsRow {...props} data-testid={values?.dimensions.key} styles={customStyle} />
            );
          };
          const rowErrors = uniq(
            errors.filters
              ?.map(e => e?.rowErrors)
              .reduce((a, b) => a.concat(b), [])
              .filter(e => e),
          );

          return (
            <Form>
              {rowErrors?.length > 0 && (
                <MessageBar messageBarType={MessageBarType.error} nowrap>
                  <div className={styles.messageHeader}>
                    <strong>Cannot save filters:</strong>
                  </div>
                  <ul style={{ listStyleType: 'disc', paddingLeft: pxToRem(30) }}>
                    {rowErrors.map(e => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </MessageBar>
              )}
              <div className={styles.filterDimensionsContainer}>
                <Label>Filter Dimensions</Label>
                <Field
                  name="dimensions"
                  component={MultiPickerFormik}
                  options={allColumns}
                  data-testid="dimension-selector"
                  multiTag
                />
              </div>
              <FieldArray name="filters">
                {arrayHelpers => {
                  const columns = [
                    {
                      key: 'exclude',
                      name: 'Exclude?',
                      maxWidth: 55,
                      onRender: (line, index) => {
                        const iconName = line.exclude ? 'CompletedSolid' : '';
                        return (
                          <IconButton
                            className={styles.excludeButton}
                            ariaLabel="select"
                            iconProps={{ iconName }}
                            onClick={() =>
                              arrayHelpers.replace(index, { ...line, exclude: !line.exclude })
                            }
                          />
                        );
                      },
                    },
                    ...values.dimensions,
                    effectiveDateColumn(minDate, maxDate),
                    expirationDateColumn(minDate, maxDate),
                    {
                      key: 'menu',
                      name: 'Menu',
                      styles: { root: { visibility: 'hidden' } },
                      maxWidth: 20,
                      onRender: (item, index) => (
                        <IconButton
                          iconProps={{ iconName: 'More' }}
                          title="Options"
                          ariaLabel="Options"
                          onRenderMenuIcon={() => null}
                          menuProps={{
                            shouldFocusOnMount: false,
                            items: [
                              {
                                key: 'duplicate',
                                text: 'Duplicate',
                                onClick: () => arrayHelpers.insert(index + 1, omit(item, '_id')),
                              },
                              {
                                key: 'delete',
                                text: 'Delete',
                                onClick: () => arrayHelpers.remove(index),
                              },
                            ],
                          }}
                        />
                      ),
                    },
                  ];
                  return (
                    <>
                      {values.filters.length > 0 && (
                        <Field
                          name="filters"
                          className={styles.filterDimensionEditor}
                          component={DynamicDetailsList}
                          columns={columns}
                          selectionMode={SelectionMode.none}
                          columnReorderOptions={columnReorderOptions}
                          arrayHelpers={arrayHelpers}
                          onRenderRow={onRenderRow}
                          data-testid="filter-columns"
                        />
                      )}
                      <div className={styles.buttonContainer}>
                        <TextButton
                          type="button"
                          className={styles.addItem}
                          data-testid="add-new-filter"
                          onClick={() => {
                            arrayHelpers.push(createNewFilter());
                          }}
                        >
                          Add New Filter
                        </TextButton>
                      </div>
                    </>
                  );
                }}
              </FieldArray>
            </Form>
          );
        }}
      </Formik>
    );
  },
);

ContractFilterForm.propTypes = {
  scopeFilter: PropTypes.shape({
    dimensions: PropTypes.arrayOf(PropTypes.string),
    filters: PropTypes.array,
  }),
  effectiveDate: PropTypes.string.isRequired,
  terminationDate: PropTypes.string.isRequired,
  onSubmit: PropTypes.func,
};

ContractFilterForm.defaultProps = {
  scopeFilter: undefined,
  onSubmit: () => {},
};

export default ContractFilterForm;
